import {
  ButtonDS,
  dismissToastDS,
  triggerToastDS,
  updateToastDS,
  UploadFileOutlined,
} from "@qivia/ui";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  selectUpdateSupportingDocumentStatus,
  selectUploadProgress,
  selectUploadSupportingDocumentStatus,
  uploadSupportingDocumentAsync,
} from "../transactionsSlice";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { transactionsSlice } from "../transactionsSlice";
import { selectManager } from "../../homeSlice";

export const UploadSupportingDocumentModal = ({
  transactionUuid,
}: {
  transactionUuid: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingToastIdRef = useRef<number | string | null>(null);
  const uploadSupportingDocumentStatus = useAppSelector(
    selectUploadSupportingDocumentStatus,
  );
  const uploadProgress = useAppSelector(selectUploadProgress);
  const updateSupportingDocumentStatus = useAppSelector(
    selectUpdateSupportingDocumentStatus,
  );
  const manager = useAppSelector(selectManager);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target?.files?.[0] || null;
      if (!file || !manager?.uuid) return;

      // Ici : upload API / S3 / backend
      const formData = new FormData();
      formData.append("file", file);
      formData.append("transactionId", transactionUuid);
      formData.append("originalFileName", file.name);
      formData.append("uploadedBy", manager.uuid);
      void dispatch(uploadSupportingDocumentAsync(formData));
    },
    [transactionUuid, dispatch, manager],
  );

  const handleUpload = useCallback(() => {
    if (!fileInputRef.current) return;
    fileInputRef.current.value = ""; // reset the file input
    fileInputRef.current.click();
  }, []);

  useEffect(() => {
    if (updateSupportingDocumentStatus === "success") {
      triggerToastDS(
        t(`transactions.uploadSupportingDocument.success`) || "",
        "SUCCESS",
      );
      dispatch(transactionsSlice.actions.resetUpdateSupportingDocumentStatus());
    } else if (updateSupportingDocumentStatus === "failed") {
      triggerToastDS(
        t("transactions.uploadSupportingDocument.failure") || "",
        "ERROR",
      );
      dispatch(transactionsSlice.actions.resetUpdateSupportingDocumentStatus());
    }
  }, [updateSupportingDocumentStatus, dispatch, t]);

  useEffect(() => {
    if (uploadSupportingDocumentStatus === "success") {
      // Fermer le toast "processing" s'il existe
      if (processingToastIdRef.current) {
        dismissToastDS(processingToastIdRef.current);
        processingToastIdRef.current = null;
      }
      dispatch(transactionsSlice.actions.resetUploadStatus());
    } else if (uploadSupportingDocumentStatus === "failed") {
      // Fermer le toast "processing" s'il existe
      if (processingToastIdRef.current) {
        dismissToastDS(processingToastIdRef.current);
        processingToastIdRef.current = null;
      }
      triggerToastDS(
        t("transactions.uploadSupportingDocument.failure") || "",
        "ERROR",
      );
      dispatch(transactionsSlice.actions.resetUploadStatus());
    }
  }, [uploadSupportingDocumentStatus, t, dispatch]);

  useEffect(() => {
    if (uploadSupportingDocumentStatus === "processing") {
      if (!processingToastIdRef.current) {
        const toastId = triggerToastDS(
          t("transactions.uploadSupportingDocument.processing") || "",
          "DOWNLOADING",
          { autoClose: false },
        );
        if (toastId) {
          processingToastIdRef.current = toastId;
        }
      } else {
        updateToastDS(
          processingToastIdRef.current,
          t("transactions.uploadSupportingDocument.processing") || "",
          "DOWNLOADING",
          uploadProgress,
        );
      }
    }
  }, [uploadSupportingDocumentStatus, t, uploadProgress]);

  return (
    <>
      <ButtonDS
        format="fill"
        buttonType="secondary"
        text={t(`transactions.uploadSupportingDocument.button`)}
        leftIcon={<UploadFileOutlined />}
        onClick={handleUpload}
      />

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*,application/pdf"
        style={{ display: "none" }}
      />
    </>
  );
};
